/**
 * Video Card Component
 * Gère la lecture vidéo avec contrôles play/pause et volume
 */

// Variable globale pour partager le volume entre toutes les vidéos
let sharedVolume = 0.15;

class VideoCard extends HTMLElement {
  constructor() {
    super();
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.endedTimeout = null;
    this.centerObserver = null;
  }

  connectedCallback() {
    this.video = this.querySelector(".video-card__video");
    this.playButton = this.querySelector(".video-card__play-button");
    this.soundButton = this.querySelector(".video-card__sound-button");
    this.volumeInput = this.querySelector(".video-card__volume-input");
    this.volumeControl = this.querySelector(".video-card__volume-control");
    this.clickOverlay = this.querySelector(".video-card__click-overlay");

    if (!this.video || !this.playButton || !this.soundButton) {
      return;
    }

    // Utiliser le volume partagé
    this.video.volume = sharedVolume;

    // Synchroniser le slider avec le volume partagé
    if (this.volumeInput) {
      this.volumeInput.value = sharedVolume * 100;
    }

    // Empêcher le drag sur les boutons et le slider
    this.#preventDragOnButton(this.playButton);
    this.#preventDragOnButton(this.soundButton);
    if (this.volumeControl) {
      this.#preventDragOnButton(this.volumeControl);
    }

    // Événements des boutons
    this.playButton.addEventListener("click", this.#handlePlayClick);
    this.soundButton.addEventListener("click", this.#handleSoundClick);

    // Gestion du clic vs drag sur l'overlay
    if (this.clickOverlay) {
      // Sur mobile, utiliser un simple click event (plus fiable avec snap scroll)
      if (window.innerWidth < 750) {
        this.clickOverlay.addEventListener("click", this.#handleOverlayClick);
      } else {
        // Sur desktop, détecter drag vs clic
        this.clickOverlay.addEventListener("mousedown", this.#handleOverlayMouseDown);
        this.clickOverlay.addEventListener("mouseup", this.#handleOverlayMouseUp);
      }
    }

    // Événement du slider de volume
    if (this.volumeInput) {
      this.volumeInput.addEventListener("input", this.#handleVolumeChange);
    }

    // Événements vidéo
    this.video.addEventListener("play", this.#handlePlay);
    this.video.addEventListener("pause", this.#handlePause);
    this.video.addEventListener("ended", this.#handleEnded);

    // Observer pour pause hors viewport
    this.#setupIntersectionObserver();

    // Observer pour lecture auto au scroll (style shorts)
    this.#setupCenterObserver();
  }

  disconnectedCallback() {
    // Cleanup
    if (this.endedTimeout) {
      clearTimeout(this.endedTimeout);
    }

    if (this.playButton) {
      this.playButton.removeEventListener("click", this.#handlePlayClick);
    }
    if (this.soundButton) {
      this.soundButton.removeEventListener("click", this.#handleSoundClick);
    }
    if (this.clickOverlay) {
      this.clickOverlay.removeEventListener("click", this.#handleOverlayClick);
      this.clickOverlay.removeEventListener("mousedown", this.#handleOverlayMouseDown);
      this.clickOverlay.removeEventListener("mouseup", this.#handleOverlayMouseUp);
    }
    if (this.volumeInput) {
      this.volumeInput.removeEventListener("input", this.#handleVolumeChange);
    }

    if (this.video) {
      this.video.removeEventListener("play", this.#handlePlay);
      this.video.removeEventListener("pause", this.#handlePause);
      this.video.removeEventListener("ended", this.#handleEnded);
    }

    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.centerObserver) {
      this.centerObserver.disconnect();
    }
  }

  #preventDragOnButton(button) {
    button.addEventListener(
      "mousedown",
      (e) => {
        e.stopPropagation();
      },
      { capture: true }
    );

    button.addEventListener(
      "touchstart",
      (e) => {
        e.stopPropagation();
      },
      { capture: true }
    );
  }

  #resetOtherVideos() {
    // Remettre à zéro TOUTES les autres vidéos (comme jamais lancées)
    const allVideoCards = document.querySelectorAll("video-card");
    allVideoCards.forEach((card) => {
      if (card !== this && card.video) {
        // Pause
        card.video.pause();
        // Reset à 0
        card.video.currentTime = 0;
        // Forcer le rechargement pour afficher le poster
        card.video.load();
        // Mute
        card.video.muted = true;
        // Retirer les classes
        card.classList.remove("is-playing");
        card.classList.remove("has-sound");
        card.classList.remove("is-centered");
      }
    });
  }

  #handlePlayClick = (e) => {
    e.stopPropagation();
    e.preventDefault();

    // Annuler le timeout de réaffichage du poster si on rejoue
    if (this.endedTimeout) {
      clearTimeout(this.endedTimeout);
      this.endedTimeout = null;
    }

    if (this.video.paused) {
      // Marquer que l'utilisateur a joué la vidéo
      this.setAttribute("data-user-played", "true");

      // Remettre à zéro TOUTES les autres vidéos AVANT (comme jamais lancées)
      this.#resetOtherVideos();

      // Marquer cette vidéo comme centrée
      this.classList.add("is-centered");

      // Démuter automatiquement et devenir la lecture principale
      this.video.muted = false;
      this.classList.add("has-sound");

      this.video.play().catch(() => {});
    } else {
      this.video.pause();
    }
  };

  #handleSoundClick = (e) => {
    e.stopPropagation();
    e.preventDefault();

    // Annuler le timeout de réaffichage du poster si on interagit
    if (this.endedTimeout) {
      clearTimeout(this.endedTimeout);
      this.endedTimeout = null;
    }

    // Toggle le son de cette vidéo
    if (this.video.muted) {
      // Remettre à zéro TOUTES les autres vidéos
      this.#resetOtherVideos();

      // Activer le son
      this.video.muted = false;
      this.classList.add("has-sound");

      // S'assurer que la vidéo joue
      if (this.video.paused) {
        this.video.play().catch(() => {});
      }
    } else {
      // Désactiver le son
      this.video.muted = true;
      this.classList.remove("has-sound");
    }
  };

  #handleOverlayClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    this.#togglePlayPause();
  };

  #handleOverlayMouseDown = (e) => {
    this.dragStartX = e.clientX;
    this.dragStartY = e.clientY;
  };

  #handleOverlayMouseUp = (e) => {
    // Calculer la distance du drag
    const deltaX = Math.abs(e.clientX - this.dragStartX);
    const deltaY = Math.abs(e.clientY - this.dragStartY);

    // Si le mouvement est inférieur à 5px, c'est un clic
    if (deltaX < 5 && deltaY < 5) {
      this.#togglePlayPause();
    }
  };

  #togglePlayPause = () => {
    // Annuler le timeout de réaffichage du poster si on rejoue
    if (this.endedTimeout) {
      clearTimeout(this.endedTimeout);
      this.endedTimeout = null;
    }

    // Toggle play/pause au clic sur la vidéo avec son
    if (this.video.paused) {
      // Marquer que l'utilisateur a joué la vidéo (pour la première vidéo sur mobile)
      this.setAttribute("data-user-played", "true");

      // Remettre à zéro toutes les autres vidéos AVANT
      this.#resetOtherVideos();

      // Marquer cette vidéo comme centrée
      this.classList.add("is-centered");

      // Activer le son automatiquement
      this.video.muted = false;
      this.classList.add("has-sound");

      // Jouer cette vidéo avec son
      this.video.play().catch(() => {});
    } else {
      // Mettre en pause
      this.video.pause();
    }
  };

  #handleVolumeChange = (e) => {
    e.stopPropagation();
    const volume = parseInt(e.target.value) / 100;

    // Mettre à jour le volume partagé
    sharedVolume = volume;

    // Appliquer le volume à TOUTES les vidéos
    const allVideoCards = document.querySelectorAll("video-card");
    allVideoCards.forEach((card) => {
      if (card.video) {
        card.video.volume = volume;
      }
      // Synchroniser tous les sliders
      if (card.volumeInput) {
        card.volumeInput.value = volume * 100;
      }
    });
  };

  #handlePlay = () => {
    this.classList.add("is-playing");
  };

  #handlePause = () => {
    this.classList.remove("is-playing");
  };

  #handleEnded = () => {
    // La vidéo s'arrête à la fin (pas de loop)
    this.classList.remove("is-playing");

    // Après 2 secondes, réafficher le poster
    this.endedTimeout = setTimeout(() => {
      // Remettre la vidéo au début
      this.video.currentTime = 0;
      // Recharger la vidéo pour afficher le poster
      this.video.load();
      // Couper le son
      this.video.muted = true;
      this.classList.remove("has-sound");
      this.endedTimeout = null;
    }, 2000);
  };

  #setupIntersectionObserver() {
    // Sur mobile, ne pas utiliser cet observer (on utilise centerObserver)
    if (window.innerWidth < 750) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && !this.video.paused) {
            this.video.pause();
            this.classList.remove("is-playing");
            if (!this.video.muted) {
              this.video.muted = true;
              this.classList.remove("has-sound");
            }
          }
        });
      },
      { threshold: 0.25 }
    );

    this.observer.observe(this);
  }

  #setupCenterObserver() {
    // Uniquement sur mobile
    if (window.innerWidth >= 750) return;

    // Vérifier si c'est la première vidéo de manière plus robuste
    const allSlides = this.closest("slideshow-component")?.querySelectorAll("slideshow-slide");
    const currentSlide = this.closest("slideshow-slide");
    const isFirstSlide = allSlides && currentSlide && allSlides[0] === currentSlide;

    // Observer strict : SEULE la vidéo vraiment au centre joue (mobile uniquement)
    this.centerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Si la vidéo est bien centrée (> 85% visible au centre)
          if (entry.isIntersecting && entry.intersectionRatio > 0.85) {
            // Marquer la vidéo comme centrée (pour garder le scale(1.05) même en pause)
            this.classList.add("is-centered");

            // Ne pas autoplay la première vidéo, MAIS ne pas la bloquer si lancée manuellement
            if (isFirstSlide && this.video.paused && !this.hasAttribute("data-user-played")) {
              return; // La première vidéo ne se lance que sur clic manuel
            }

            // Remettre à zéro les autres vidéos et lancer celle-ci
            if (this.video.paused) {
              this.#resetOtherVideos();

              this.setAttribute("data-autoplayed", "true");
              this.video.muted = false;
              this.classList.add("has-sound");
              this.video.play().catch(() => {});
            }
          } else if (entry.intersectionRatio < 0.6) {
            // Retirer la classe is-centered quand la vidéo sort du centre
            this.classList.remove("is-centered");

            // Si la vidéo n'est plus assez centrée (< 60%), la mettre en pause
            if (!this.video.paused && !(isFirstSlide && !this.hasAttribute("data-user-played"))) {
              this.video.pause();
              this.removeAttribute("data-autoplayed");
            }
          }
        });
      },
      {
        threshold: [0.6, 0.85], // Seuils stricts pour une seule vidéo
        root: null,
        rootMargin: "-10% 0px -10% 0px", // Zone centrale stricte (80% du milieu)
      }
    );

    this.centerObserver.observe(this);
  }
}

if (!customElements.get("video-card")) {
  customElements.define("video-card", VideoCard);
}